import { createSelector } from "@reduxjs/toolkit";

import { VehicleSettings } from "@skydio/channels/src/update_vehicle_settings_pb";
import { NavigationMode } from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/navigation_mode_pb";

import { compareCurrentlyEditableSettings } from "./utils";

import type { AppState } from "../store";

export const getCurrentVehicleSettingsProto = (state: AppState) =>
  state.vehicleSettings.vehicleSettingsProto;
export const getCurrentDesiredVehicleSettingsProto = (state: AppState) =>
  state.vehicleSettings.desiredVehicleSettingsProto;
export const getCurrentVehicleSettingsRequestState = (state: AppState) =>
  state.vehicleSettings.vehicleSettingsRequestState;
export const getIsOASettingsModalOpen = (state: AppState) =>
  state.vehicleSettings.isOASettingsModalOpen;
export const getDesiredVehicleSettingsHasChanges = createSelector(
  getCurrentVehicleSettingsProto,
  getCurrentDesiredVehicleSettingsProto,
  (current, desired) => !compareCurrentlyEditableSettings(current, desired)
);

export const getCurrentObstacleMarginSetting = createSelector(
  getCurrentVehicleSettingsProto,
  settingsProto =>
    settingsProto?.getObstacleMarginMode() ?? VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES
);

export const getCurrentNavigationMode = createSelector(
  getCurrentVehicleSettingsProto,
  settingsProto => settingsProto?.getNavigationMode()
);

export const getCurrentRTDSettings = createSelector(getCurrentVehicleSettingsProto, settingsProto =>
  settingsProto?.getReturnSettings()
);

export const getCurrentVehicleReturnUsingBacktrackSetting = createSelector(
  getCurrentRTDSettings,
  settings => settings?.getUseBacktrack()
);

export const getCurrentVehicleReturnUsingGlobalPathfinderSetting = createSelector(
  getCurrentRTDSettings,
  settings => settings?.getUseGlobalPathfinder()
);

export const getCurrentStopAtStructureSetting = createSelector(
  getCurrentVehicleSettingsProto,
  settings => settings?.getStopAtStructure()
);

export const getCurrentStopAtStructureSpeed = createSelector(
  getCurrentVehicleSettingsProto,
  settingsProto =>
    settingsProto?.getSpeedNearObstacles() !== undefined
      ? parseFloat(settingsProto.getSpeedNearObstacles().toFixed(1))
      : undefined
);

export const getCurrentIlluminationMode = createSelector(
  getCurrentVehicleSettingsProto,
  settingsProto => settingsProto?.getIlluminationMode()
);

export const getCurrentEnableRapidDescent = createSelector(
  getCurrentVehicleSettingsProto,
  settingsProto => settingsProto?.getEnableRapidDescent()
);

export const getCurrentDisableFrameLock = createSelector(
  getCurrentVehicleSettingsProto,
  settingsProto => settingsProto?.getDisableFrameLock()
);

export const isVehicleSettingsRequestInFlight = createSelector(
  getCurrentVehicleSettingsRequestState,
  vehicleSettingsRequestState => vehicleSettingsRequestState.status === "inflight"
);
export const isVehicleSettingsRequestReady = createSelector(
  getCurrentVehicleSettingsRequestState,
  vehicleSettingsRequestState => vehicleSettingsRequestState.status === "ready"
);
export const getIsLowLightOnNightsenseDisabled = createSelector(
  getCurrentNavigationMode,
  getCurrentVehicleSettingsProto,
  (navigationMode, currentState) => {
    const nightsenseDisabled = currentState.getNightsenseDisabled();
    return navigationMode === NavigationMode.Mode.NIGHT_AUTONOMY && nightsenseDisabled;
  }
);
export const getHasReceivedCurrentVehicleSettings = createSelector(
  getCurrentVehicleSettingsProto,
  // undefined or null validationId means we haven't received the current vehicle settings
  vehicleSettingsProto => vehicleSettingsProto.getValidationId() != null
);

export const getVehicleWifiBandSettings = createSelector(
  getCurrentVehicleSettingsProto,
  settingsProto => ({
    wifiBand: settingsProto.getWifiBand(),
    bandList: settingsProto.getBandSelectionListList(),
    wifiChannelList: settingsProto.getWifiChannelListList(),
    wifiCountryCode: settingsProto.getWifiCountryCode(),
    manualWifiChannel: settingsProto.getManualWifiChannel(),
    manualBandSelection: settingsProto.getManualBandSelection(),
  })
);
